import { createZodSchematizer } from 'tinybase/schematizers/schematizer-zod';
import { z } from 'zod';

import type { TablesSchemaFromZod } from '../types/tinybase';

import { caseEventSchema, caseSchema } from '../schemas/cases';
import { userMessageSchema } from '../schemas/messages';
import { fireSafetyNoteSchema, noteSchema } from '../schemas/notes';
import { pcfraDocumentSchema, pcfraDraftSchema, pcfraReferrerSchema } from '../schemas/pcfra';
import { contactDetailsItemSchema, pcfraPersonSchema, personSchema } from '../schemas/people';
import { visitSchema } from '../schemas/visits';

export type UserStoreZodTables = typeof userStoreTables;

const schematizer = createZodSchematizer();

/**
 * Zod object schemas are the source of truth for each table. Tables with a
 * domain schema reference it directly from `common/schemas/*` (where enum/date
 * refinements live); the schematizer reduces those to basic cell types.
 * Tables without a domain schema are defined inline.
 */
export const userStoreTables = {
  userDirectory: z.object({
    camdenAccountName: z.string(),
    id: z.string(),
    email: z.string(),
    name: z.string(),
    department: z.string(),
    jobTitle: z.string(),
  }),
  messages: userMessageSchema,
  /**
   * Used to map the temp IDs of pending items to their real IDs
   * upon being written to their source system
   * */
  tempIdMaps: z.object({
    tempId: z.string(),
    parentTempId: z.string().optional(),
    realId: z.string().optional(),
    parentRealId: z.string().optional(),
    timestamp: z.string(),
  }),
  propertyDetails: z.object({
    propertyId: z.number(),
    accountNumber: z.number(),
    payReference: z.string(),
    rentBalance: z.number(),
    rentTotalPayable: z.number(),
    tenancySource: z.string(),
    tenancyStartDate: z.string(),
    numberOfBedrooms: z.number(),
    floorLevel: z.string(),
    adaptation: z.string(),
    heatingType: z.string(),
    accessibleLift: z.string(),
    nextOfKinName: z.string(),
    nextOfKinTelephone: z.string(),
    nextOfKinAddress: z.string(),
    noResidentsFound: z.boolean(),
  }),
  pcfraPropertyDetails: z.object({
    propertyId: z.number(),
    floorLevel: z.string(),
    noResidentsFound: z.boolean(),
  }),
  tenancies: z.object({
    tenancyId: z.number(),
    propertyId: z.number(),
  }),
  people: personSchema,
  pcfraPeople: pcfraPersonSchema,
  peopleRiskFactors: z.object({
    itemId: z.string(),
    propertyId: z.number(),
    personId: z.number(),
    key: z.string(),
    value: z.string(),
    description: z.string(),
  }),
  peopleAttributes: z.object({
    attributeId: z.number(),
    propertyId: z.number(),
    personId: z.number(),
    attributeCode: z.string(),
    categoryCode: z.string(),
    value: z.string(),
  }),
  contactDetails: contactDetailsItemSchema,
  notes: noteSchema,
  fireSafetyNotes: fireSafetyNoteSchema,
  serviceRequests: z.object({
    serviceRequestId: z.number(),
    isPropertyRequest: z.boolean(),
    buildingId: z.string(),
    propertyId: z.number(),
    description: z.string(),
    createdDate: z.string(),
    reportedDate: z.string(),
    targetDate: z.string(),
    modifiedDate: z.string(),
    statusCode: z.string(),
    status: z.string(),
    priorityCode: z.string(),
    priorityType: z.string(),
    assauId: z.string(),
    assauType: z.string(),
    programType: z.string(),
  }),
  worksOrders: z.object({
    worksOrderId: z.number(),
    serviceRequestId: z.number(),
    propertyId: z.number(),
    sequenceNo: z.number(),
    status: z.string(),
    description: z.string(),
    createdDate: z.string(),
    raisedDate: z.string(),
    modifiedDate: z.string(),
  }),
  housingApplications: z.object({
    applicationId: z.number(),
    propertyId: z.number(),
    tenancyId: z.number(),
    offerFlag: z.string(),
    nominationFlag: z.string(),
    receivedDate: z.iso.date().optional(),
    correspondenceName: z.string(),
    status: z.string(),
    statusDate: z.iso.date().optional(),
    acceptedHomelessDate: z.iso.date().optional(),
    expectedHomelessDate: z.iso.date().optional(),
    homelessReason: z.string(),
    homelessCaseOrigin: z.string(),
    becameActiveDate: z.iso.datetime({ offset: true }).optional(),
    applicationSource: z.string(),
    adviceCaseRef: z.string(),
  }),
  applicationApplicants: z.object({
    applicantId: z.string(),
    applicationId: z.number(),
    propertyId: z.number(),
    personId: z.number(),
  }),
  applicationAllocations: z.object({
    allocationId: z.string(),
    applicationId: z.number(),
    rule: z.string(),
    ruleDescription: z.string(),
    points: z.number(),
  }),
  arrearsArrangements: z.object({
    arrangementId: z.number(),
    propertyId: z.number(),
    tenancyId: z.number(),
    balance: z.number(),
    createdBy: z.string(),
    created: z.string(),
    startDate: z.string(),
    intervalPeriod: z.number(),
    intervalUnits: z.string(),
    hasTakenFirstDd: z.boolean(),
    amount: z.number(),
    actionCode: z.string(),
    actionDescription: z.string(),
  }),
  cases: caseSchema,
  caseEvents: caseEventSchema,
  caseSupportingOfficers: z.object({
    supportingOfficerId: z.number(),
    caseId: z.number(),
    officer: z.string(),
  }),
  visits: visitSchema,
  pcfraReferrers: pcfraReferrerSchema,
  pcfraDrafts: pcfraDraftSchema,
  pcfraDocuments: pcfraDocumentSchema,
} as const;

export const userStoreTablesSchema = schematizer.toTablesSchema(
  userStoreTables,
) as TablesSchemaFromZod<UserStoreZodTables>;

export const userStoreValuesSchema = schematizer.toValuesSchema({
  /** Toggles server-side listeners `BaseUserStore.setActive/setInactive` */
  isActive: z.boolean(),
}) as {
  isActive: { type: 'boolean' };
};
